<template>
    <div class="w-full px-[2px]">
        <div
            v-if="!noLabel || hasTranslations"
            :class="[
                'flex gap-x-2',
                labelmargin,
                { 'items-center': !hasTranslations },
                { 'justify-between items-end': hasTranslations },
                { 'w-5/6': $slots.buttons },
            ]"
        >
            <InputLabel
                v-if="!noLabel"
                :id="id"
                :value="label"
                :description="description"
                :required="required"
                :disabled="disabled"
            />
            <LocalePicker
                v-if="hasTranslations"
                :locales="translations"
                :localesWithMissingContent="localesWithMissingContent"
                v-model="currentTranslation"
            />
            <slot name="label-right" />
        </div>
        <div
            :class="[
                'relative',
                {
                    'flex justify-between items-center space-x-5':
                        $slots.buttons,
                },
            ]"
        >
            <Transition name="fade" mode="out-in">
                <input
                    :key="currentTranslation"
                    :id="id"
                    :autocomplete="autocomplete"
                    :disabled="disabled"
                    :type="type"
                    ref="input"
                    class="input"
                    :class="[
                        'rounded-input placeholder:text-input-placeholder border border-input-border hover:border-input-hover-border focus:outline-none focus:input-focus-border focus:border-input-focus-border focus:ring-input-focus-border w-full text-input transition-shadows duration-input-active',
                        {
                            'border-danger hover:border-danger-hover focus:border-danger-active focus:ring-danger-active':
                                error,
                        },
                        {
                            'opacity-50 cursor-not-allowed bg-gray-50':
                                disabled,
                        },
                        { 'w-5/6': $slots.buttons },
                        sizeClasses,
                        inputClasses,
                        paddingClasses,
                    ]"
                    :placeholder="placeholder"
                    :value="currentText"
                    @input="updateModelValue"
                    @blur="$emit('blur')"
                    @keydown.esc="$emit('keydown.esc')"
                    @keydown.enter="$emit('keydown.enter')"
                />
            </Transition>
            <slot name="buttons" />
            <slot name="absolute" />
        </div>
        <p
            v-if="disabled && disabledText"
            class="text-input-muted text-sm mt-2"
            v-text="disabledText"
        />
        <InputError :message="error" :class="{ 'mt-2': size == 'base' }" />
    </div>
</template>

<script>
import InputLabel from '@/Components/FormElements/InputLabel.vue'
import InputError from '@/Components/FormElements/InputError.vue'
import LocalePicker from '@/Components/FormElements/LocalePicker.vue'
export default {
    components: {
        InputLabel,
        InputError,
        LocalePicker,
    },

    emits: ['update:modelValue', 'keydown.esc', 'keydown.enter', 'blur'],

    expose: ['focusMethod', 'isMissingTranslations'],

    props: {
        modelValue: {
            type: [String, Object, Number, null],
            required: true,
            validator: (value, props) => {
                if (
                    typeof value === 'string' &&
                    props.translations.length === 0
                ) {
                    return true
                }
                if (
                    typeof value === 'object' &&
                    props.translations.length > 0
                ) {
                    return true
                }
                if (typeof value === 'number' && props.type === 'number') {
                    return true
                }
                return value == null
            },
        },
        label: {
            type: String,
            default: null,
        },
        description: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledText: {
            type: String,
            default: null,
        },
        id: {
            type: String,
            required: true,
        },
        translations: {
            type: Array,
            default: [],
        },
        initialTranslation: {
            type: String,
            default: null,
        },
        autocomplete: {
            type: String,
            default: 'off',
        },
        type: {
            type: String,
            default: 'text',
        },
        inputClasses: {
            type: [String, Array],
            required: false,
        },
        size: {
            type: String,
            default: 'base',
            validator: (value) => ['base', 'sm', 'md'].includes(value),
        },
        noLabel: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            currentTranslation: null,
        }
    },

    created() {
        if (this.hasTranslations) {
            if (this.initialTranslation) {
                this.currentTranslation = this.initialTranslation
            } else if (
                this.translations.some(
                    (translation) => translation.id === this.$page.props.locale
                )
            ) {
                this.currentTranslation = this.$page.props.locale
            } else {
                this.currentTranslation = this.translations[0].id
            }
        }
    },

    computed: {
        paddingClasses() {
            let classes = []
            if (this.size === 'base') {
                classes.push('p-input')
            } else if (this.size === 'sm') {
                classes.push('p-input-sm')
            } else if (this.size === 'md') {
                classes.push('p-input-md')
            }
            return classes
        },
        labelmargin() {
            if (this.size === 'base') {
                return 'mb-2'
            } else if (this.size === 'sm') {
                return 'mb-0'
            } else if (this.size === 'md') {
                return 'mb-1'
            }
        },

        sizeClasses() {
            if (this.size === 'sm') {
                return 'text-sm '
            }
        },

        hasTranslations() {
            return this.translations.length > 0
        },

        currentText() {
            if (this.hasTranslations) {
                return this.modelValue[this.currentTranslation]
            }
            return this.modelValue
        },

        /**
         * Goes through each translation and check if the modelValues value for that translation is missing.
         * It will return an array with the id of the locale for the translations that are missing.
         */
        localesWithMissingContent() {
            return this.translations.reduce((acc, translation) => {
                if (!this.modelValue[translation.id]) {
                    acc.push(translation.id)
                }
                return acc
            }, [])
        },
    },

    methods: {
        updateModelValue(event) {
            if (this.hasTranslations) {
                this.$emit('update:modelValue', {
                    ...this.modelValue,
                    [this.currentTranslation]: event.target.value,
                })
            } else {
                this.$emit('update:modelValue', event.target.value)
            }
        },

        focusMethod() {
            this.$refs.input.focus()
        },

        /**
         * A function that defines how to check if a translation is missing
         * in the given modelValue
         */
        isMissingTranslations(modelValue, translations) {
            return translations.some(
                (translation) => !modelValue[translation.id]
            )
        },
    },
}
</script>
